import React, {useRef, useState} from 'react'
import {Group, Input as RACInput} from 'react-aria-components'
import styled from 'styled-components'

const OtpGroup = styled(Group)`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
`

const OtpDigitBox = styled(RACInput)`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  border: 1.5px solid ${p => p.theme.colors.grey6};
  border-radius: 8px;
  background: ${p => p.theme.colors.grey3};
  outline: none;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;

  &[data-focused] {
    border-color: ${p => p.theme.colors.blue10};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.blue3};
    background: #fff;
  }
`

const OTP_LENGTH = 6

export const OtpCodeInput = ({
  onChange
}: {
  onChange: (code: string) => void
}) => {
  const [digits, setDigits] = useState<readonly string[]>(
    Array(OTP_LENGTH).fill('')
  )
  const refs = useRef<Array<HTMLInputElement | null>>(
    Array(OTP_LENGTH).fill(null)
  )

  const update = (next: readonly string[]) => {
    setDigits(next)
    onChange(next.join(''))
  }

  const handleChange = (i: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = digits.map((d, idx) => (idx === i ? digit : d))
    update(next)
    if (digit && i < OTP_LENGTH - 1) refs.current[i + 1]?.focus()
  }

  const handleKeyDown = (
    i: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus()
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus()
    } else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) {
      refs.current[i + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH)
    const next = Array(OTP_LENGTH)
      .fill('')
      .map((_, i) => pasted[i] ?? '')
    update(next)
    const lastFilled = Math.min(pasted.length, OTP_LENGTH - 1)
    refs.current[lastFilled]?.focus()
  }

  return (
    <OtpGroup aria-label="One-time password">
      {Array.from({length: OTP_LENGTH}, (_, i) => (
        <OtpDigitBox
          key={i}
          ref={el => {
            refs.current[i] = el
          }}
          aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
          inputMode="numeric"
          autoFocus={i === 0}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digits[i]}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
        />
      ))}
    </OtpGroup>
  )
}
